import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { shipmentMissingService } from "../../services/shipmentMissingService"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import { ArrowLeft, Pencil, AlertTriangle, ArrowRight } from "lucide-react";
import { PageLayout, Button, StatusBadge, DetailCard } from "../common/index.js";

const LABEL_CLS = "block text-xs font-medium text-secondary mb-1"
const INPUT_CLS =
  "w-full px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
const CARD_CLS = "bg-white rounded-card shadow-card p-3 sm:p-4 mb-4"

const EditMissing = () => {
  const { missingId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [missingData, setMissingData] = useState(null)
  const [rejectionReason, setRejectionReason] = useState("")

  useEffect(() => {
    fetchMissing()
  }, [missingId])

  const fetchMissing = async () => {
    try {
      setLoading(true)
      const response = await shipmentMissingService.getMissing(missingId)
      const missing = response.data.missing || response.data.data

      if (missing) {
        setMissingData(missing)
      } else {
        Alert.error("Missing shipment record not found")
      }
    } catch (error) {
      console.error("Error fetching missing:", error)
      Alert.error(error.response?.data?.error || "Failed to load missing shipment")
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async () => {
    const confirmed = await Alert.confirm(
      "Are you sure you want to approve this missing shipment report?",
      "Approve Missing Report",
    )

    if (!confirmed) return

    try {
      setUpdating(true)

      await shipmentMissingService.approveMissing(missingId)

      Alert.success("Missing shipment report approved successfully!")
      navigate("/missing")
    } catch (error) {
      console.error("Error approving missing:", error)
      Alert.error(error.response?.data?.error || "Failed to approve missing shipment")
    } finally {
      setUpdating(false)
    }
  }

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      Alert.error("Please provide a rejection reason")
      return
    }

    if (rejectionReason.trim().length < 10) {
      Alert.error("Rejection reason must be at least 10 characters")
      return
    }

    const confirmed = await Alert.confirm(
      "Are you sure you want to reject this missing shipment report?",
      "Reject Missing Report",
    )

    if (!confirmed) return

    try {
      setUpdating(true)

      await shipmentMissingService.rejectMissing(missingId, rejectionReason.trim())

      Alert.success("Missing shipment report rejected successfully!")
      navigate("/missing")
    } catch (error) {
      console.error("Error rejecting missing:", error)
      Alert.error(error.response?.data?.error || "Failed to reject missing shipment")
    } finally {
      setUpdating(false)
    }
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      approved: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200",
    }
    return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200"
  }

  if (loading) {
    return (
      <PageLayout title="Review Missing Report" icon={AlertTriangle}>
        <div className="flex items-center justify-center py-16 text-center">
          <div>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
            <p className="text-gray-600">Loading missing shipment...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!missingData) {
    return (
      <PageLayout title="Review Missing Report" icon={AlertTriangle}>
        <div className="flex items-center justify-center py-16">
          <div className="bg-white rounded-card shadow-card border p-8 max-w-md text-center">
            <p className="text-red-600 text-lg font-bold mb-2">Missing Record Not Found</p>
            <Button onClick={() => navigate("/missing")}>Back to Missing</Button>
          </div>
        </div>
      </PageLayout>
    )
  }

  const isPending = missingData.approval_status?.toLowerCase() === "pending"

  return (
    <PageLayout
      title={`${isPending ? 'Review' : 'View'} Missing Report`}
      icon={AlertTriangle}
      maxWidth="max-w-7xl"
      actions={
        <>
          <StatusBadge status={missingData.approval_status} />
          <Button variant="outline" onClick={() => navigate(`/shipments/view/${missingData.shipment_id}`)}>
            View Shipment
          </Button>
          <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/missing')}>
            Back
          </Button>
        </>
      }
    >

                {/* Missing Details Card */}
                <div className={CARD_CLS}>
                  <h3 className="text-sm font-semibold text-secondary mb-2 pb-2 border-b border-gray-200">
                    Missing Details
                  </h3>

                  <div className="pt-2">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-8">
                      <div className="p-0">
                        <p className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Missing ID</p>
                        <p className="text-base sm:text-sm font-semibold text-secondary">#{missingData.missing_id}</p>
                      </div>
                      <div className="p-0">
                        <p className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Shipment ID</p>
                        <button
                          onClick={() => navigate(`/shipments/view/${missingData.shipment_id}`)}
                          className="text-base sm:text-sm font-semibold text-secondary hover:underline"
                        >
                          #{missingData.shipment_id}
                        </button>
                      </div>
                      <div className="p-0">
                        <p className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Created By</p>
                        <p className="text-base sm:text-sm font-semibold text-secondary">{missingData.created_by || "N/A"}</p>
                      </div>
                      <div className="p-0">
                        <p className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Quantity Missing</p>
                        <p className="text-base sm:text-sm font-semibold text-secondary">{missingData.quantity_missing || "N/A"}</p>
                      </div>
                      <div className="p-0">
                        <p className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Created Date</p>
                        <p className="text-base sm:text-sm font-semibold text-secondary">
                          {missingData.created_at
                            ? new Date(missingData.created_at).toLocaleString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "N/A"}
                        </p>
                      </div>
                      <div className="p-0">
                        <p className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Status</p>
                        <span
                          className={`text-base sm:text-sm font-semibold text-secondary px-3 py-1 uppercase ${getStatusBadgeColor(missingData.approval_status).replace("border-", "")}`}
                        >
                          {missingData.approval_status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reason for Missing */}
                <div className={CARD_CLS}>
                  <h3 className="text-sm font-semibold text-secondary mb-2 pb-2 border-b border-gray-200">
                    Reason for Missing
                  </h3>
                  <div className="pt-2">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{missingData.reason}</p>
                  </div>
                </div>

                {missingData.missing_details && (
                  <div className={CARD_CLS}>
                    <h3 className="text-sm font-semibold text-secondary mb-2 pb-2 border-b border-gray-200">Additional Details</h3>
                    <div className="pt-2">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{missingData.missing_details}</p>
                    </div>
                  </div>
                )}

                {missingData.rejection_reason && (
                  <div className={CARD_CLS}>
                    <h3 className="text-sm font-semibold text-secondary mb-2 pb-2 border-b border-gray-200">
                      Rejection Reason
                    </h3>
                    <div className="pt-2">
                      <p className="text-sm text-red-900 whitespace-pre-wrap">{missingData.rejection_reason}</p>
                    </div>
                  </div>
                )}

                {isPending && (
                  <div className={CARD_CLS}>
                    <h3 className="text-sm font-semibold text-secondary mb-2 pb-2 border-b border-gray-200">
                      Approval Action
                    </h3>

                    <div className="space-y-4 pt-2">
                      <div>
                        <label className={LABEL_CLS}>
                          Rejection Reason
                          <span className="text-gray-500 text-xs ml-2 font-normal">(Required if rejecting)</span>
                        </label>
                        <textarea
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          placeholder="Provide a detailed reason if you're rejecting this missing report..."
                          rows="4"
                          className={`${INPUT_CLS} resize-none`}
                          disabled={updating}
                        />
                        <div className="flex items-center justify-between mt-2 text-xs">
                          <span
                            className={`font-semibold ${rejectionReason.length < 10 ? "text-red-600" : "text-green-600"}`}
                          >
                            {rejectionReason.length} / 10 characters minimum
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-gray-100">
                        <Button variant="primary" onClick={handleApprove} disabled={updating} loading={updating}>
                          Approve Report
                        </Button>
                        <Button
                          variant="danger"
                          onClick={handleReject}
                          disabled={updating || rejectionReason.trim().length < 10}
                          loading={updating}
                        >
                          Reject Report
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {!isPending && (
                  <div className={CARD_CLS}>
                    <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate("/missing")}>
                      Back to Missing Shipments
                    </Button>
                  </div>
                )}
    </PageLayout>
  )
}

export default EditMissing;